import IconBtn from "../../ui/button/IconBtn";
import CustomTooltip from "../../ui/CustomTooltip";
import { BagIcon, BuildingIcon, FollowUpIcon, ProductsIcon, QuotationIcon, TickIcon } from "../../icons/otherIcons";
import { BellIcon, DeleteIcon, RoundedUser } from "../../icons/btnIcons";
import { formatTime } from "../../utility/functions";
import { MODULE_META, moduleKey } from "./notificationUtils";

// Icon per notification type (also used by the notifications table)
export const MODULE_ICON = {
  lead: BagIcon,
  quotation: QuotationIcon,
  followup: FollowUpIcon,
  customer: BuildingIcon,
  product: ProductsIcon,
  user: RoundedUser,
  other: BellIcon,
};

/**
 * One notification row (Bootstrap list-group item), used by the side panel and the page.
 * compact: side panel size. lines: how many lines of the message to show (rest gets "…").
 * onMarkRead / onDelete show as hover buttons when given.
 */
const NotificationItem = ({
  item,
  compact = false,
  lines = compact ? 2 : 0,
  onOpen,
  onMarkRead,
  onDelete,
  busy,
}) => {
  const key = moduleKey(item.module);
  const meta = MODULE_META[key];
  const Icon = MODULE_ICON[key];
  const isUnread = !item.read_at;
  const hasActions = (onMarkRead && isUnread) || onDelete;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item);
        }
      }}
      className={`notif-item list-group-item list-group-item-action d-flex align-items-start gap-6 border-0 border-bottom ${
        compact ? "px-10 py-6" : "px-10 py-7"
      } ${isUnread ? "is-unread" : ""}`}
      aria-label={`${isUnread ? "Unread. " : ""}${item.title || meta.label}`}
    >
      <i
        className={`flex-center ratio-1 rounded-circle flex-shrink-0 bg-${meta.color}-subtle text-${meta.color}`}
        style={{ width: compact ? 36 : 40 }}
      >
        <Icon size={compact ? 16 : 18} />
      </i>

      <div className="flex-grow-1 min-w-0 flex-column gap-1">
        <div className="d-flex align-items-start justify-content-between gap-4">
          <span className={`fs-sm text-primary text-truncate ${isUnread ? "fw-medium" : ""}`}>
            {item.title || meta.label}
          </span>
          <span className="fs-xs text-secondary text-nowrap flex-shrink-0">
            {formatTime(item.created_at)}
          </span>
        </div>

        {item.message && (
          <div
            className="notif-message fs-xs text-secondary"
            style={
              lines
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: lines,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : undefined
            }
            title={lines ? item.message?.replace(/<[^>]*>/g, "") : undefined}
            dangerouslySetInnerHTML={{ __html: item.message }}
          />
        )}

        <div className="d-flex align-items-center gap-4 mt-1">
          <span className={`badge rounded-pill fw-normal bg-${meta.color}-subtle text-${meta.color}`}>
            {meta.label}
          </span>
          {item.action && !compact && (
            <span className="fs-xs text-secondary text-capitalize">{item.action}</span>
          )}
        </div>
      </div>

      {/* Unread dot; on hover the actions take its place */}
      <div className="notif-side d-flex align-items-center gap-2 flex-shrink-0">
        {isUnread && <span className="notif-dot rounded-circle bg-primary" aria-hidden="true" />}
        {hasActions && (
          <span className="notif-actions d-inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
            {onMarkRead && isUnread && (
              <CustomTooltip content="Mark as read" position="bottom" className="text-nowrap">
                <span className="d-inline-flex">
                  <IconBtn
                    variant="outline-none"
                    className="cd-tool-btn text-success"
                    disabled={busy}
                    onClick={() => onMarkRead(item)}
                    icon={<TickIcon size={14} />}
                    aria-label="Mark as read"
                  />
                </span>
              </CustomTooltip>
            )}
            {onDelete && (
              <CustomTooltip content="Delete" position="bottom" className="text-nowrap cd-tip-end">
                <span className="d-inline-flex">
                  <IconBtn
                    variant="outline-none"
                    className="cd-tool-btn text-danger"
                    disabled={busy}
                    onClick={() => onDelete(item)}
                    icon={<DeleteIcon size={14} />}
                    aria-label="Delete"
                  />
                </span>
              </CustomTooltip>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
